import api from './api';
import type { CommentListResult, CommunityComment, CommunityReply } from '../types/community';

interface ApiEnvelope {
  success?: boolean;
  Success?: boolean;
  message?: string | object;
  Message?: string | object;
  data?: unknown;
  Data?: unknown;
}

function isSuccess(body: ApiEnvelope): boolean {
  return body.success === true || body.Success === true;
}

function getErrorMessage(body: ApiEnvelope, fallback: string): string {
  const message = body.message ?? body.Message;
  if (typeof message === 'string' && message) return message;
  const data = body.data ?? body.Data;
  if (typeof data === 'string' && data) return data;
  return fallback;
}

function unwrapPayload(body: ApiEnvelope): Record<string, unknown> | null {
  if (!isSuccess(body)) return null;
  const message = body.message ?? body.Message;
  const data = body.data ?? body.Data;
  if (message && typeof message === 'object') return message as Record<string, unknown>;
  if (data && typeof data === 'object') return data as Record<string, unknown>;
  return null;
}

function normalizeReply(raw: Record<string, unknown>): CommunityReply {
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    text: String(raw.text ?? raw.Text ?? ''),
    studentId: (raw.studentId ?? raw.StudentId ?? undefined) as string | undefined,
    studentName: (raw.studentName ?? raw.StudentName ?? undefined) as string | undefined,
    moderatorId:
      raw.moderatorId != null || raw.ModeratorId != null
        ? String(raw.moderatorId ?? raw.ModeratorId)
        : undefined,
    moderatorName: (raw.moderatorName ?? raw.ModeratorName ?? undefined) as string | undefined,
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
  };
}

function normalizeComment(raw: Record<string, unknown>): CommunityComment {
  const repliesRaw = (raw.replies ?? raw.Replies ?? []) as Record<string, unknown>[];
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    text: String(raw.text ?? raw.Text ?? ''),
    studentId: (raw.studentId ?? raw.StudentId ?? undefined) as string | undefined,
    studentName: (raw.studentName ?? raw.StudentName ?? undefined) as string | undefined,
    moderatorId:
      raw.moderatorId != null || raw.ModeratorId != null
        ? String(raw.moderatorId ?? raw.ModeratorId)
        : undefined,
    moderatorName: (raw.moderatorName ?? raw.ModeratorName ?? undefined) as string | undefined,
    status: String(raw.status ?? raw.Status ?? 'Pending'),
    parentId:
      raw.parentId != null || raw.ParentId != null
        ? String(raw.parentId ?? raw.ParentId)
        : undefined,
    levelType: (raw.levelType ?? raw.LevelType ?? undefined) as CommunityComment['levelType'],
    levelId:
      raw.levelId != null || raw.LevelId != null ? String(raw.levelId ?? raw.LevelId) : undefined,
    createdAt: String(raw.createdAt ?? raw.CreatedAt ?? ''),
    updatedAt: (raw.updatedAt ?? raw.UpdatedAt ?? undefined) as string | undefined,
    replies: Array.isArray(repliesRaw) ? repliesRaw.map(normalizeReply) : [],
  };
}

function normalizeList(
  payload: Record<string, unknown>,
  fallbackPage: number,
  fallbackPageSize: number
): CommentListResult {
  const commentsRaw = (payload.comments ?? payload.Comments ?? payload) as unknown;
  const list = Array.isArray(commentsRaw)
    ? commentsRaw
    : Array.isArray((payload as { items?: unknown[] }).items)
      ? (payload as { items: unknown[] }).items
      : [];

  return {
    comments: (list as Record<string, unknown>[]).map(normalizeComment),
    totalCount: Number(payload.totalCount ?? payload.TotalCount ?? list.length),
    page: Number(payload.page ?? payload.Page ?? fallbackPage),
    pageSize: Number(payload.pageSize ?? payload.PageSize ?? fallbackPageSize),
    totalPages: Number(payload.totalPages ?? payload.TotalPages ?? 1),
  };
}

export const communityService = {
  async getGradeComments(grade: string, page = 1, pageSize = 10, status = 'Approved') {
    try {
      const response = await api.get<ApiEnvelope>(
        `/api/Community/grade/${encodeURIComponent(grade)}/comments`,
        { params: { status, page, pageSize } }
      );
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر تحميل تعليقات المنتدى') };
      }
      return { success: true as const, data: normalizeList(payload, page, pageSize) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'تعذر تحميل تعليقات المنتدى')
        : 'تعذر تحميل تعليقات المنتدى';
      return { success: false as const, error: message };
    }
  },

  async getMyPendingComments() {
    try {
      const response = await api.get<ApiEnvelope>('/api/Community/my-pending');
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'تعذر تحميل أسئلتك') };
      }

      const list = Array.isArray(payload)
        ? payload
        : ((payload.comments ?? payload.Comments ?? payload.data ?? payload.Data ?? []) as unknown[]);

      return {
        success: true as const,
        data: (Array.isArray(list) ? list : []).map((item) =>
          normalizeComment(item as Record<string, unknown>)
        ),
      };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'تعذر تحميل أسئلتك')
        : 'تعذر تحميل أسئلتك';
      return { success: false as const, error: message };
    }
  },

  async createGradeComment(grade: string, text: string) {
    try {
      const response = await api.post<ApiEnvelope>(
        `/api/Community/grade/${encodeURIComponent(grade)}/comments`,
        {
          text: text.trim(),
          levelType: 'Grade',
          levelId: grade,
        }
      );
      const body = response.data;
      const payload = unwrapPayload(body);
      if (!payload) {
        return { success: false as const, error: getErrorMessage(body, 'فشل إرسال السؤال') };
      }
      return { success: true as const, data: normalizeComment(payload) };
    } catch (error: unknown) {
      const err = error as { response?: { data?: ApiEnvelope } };
      const message = err.response?.data
        ? getErrorMessage(err.response.data, 'فشل إرسال السؤال')
        : 'فشل إرسال السؤال';
      return { success: false as const, error: message };
    }
  },
};
